import { Injectable } from '@angular/core';
import { Http, QueryBus, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { ProductRepositoryContract } from '@skylab-shared/business';
import { DeleteResponse, HttpService, ObservableCollection, QueryBus as QueryBusBase, Repository, RepositoryBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { ProductDTO, ProductModel, ProductOssVatRateModel, ProductPriceModel } from '@skylab-shared/dataModel';
import { pickBy } from '@skylab-shared/utils';
import { environment } from '@skylab/environments';
import { ProductBO } from './product.bo';

@Repository({
    boType: ProductBO,
    apiController: TaggedUrl.join('products', [Tags.Api]),
    dto: {
        get: ProductDTO,
        update: ProductDTO
    }
})
@Injectable({ providedIn: 'root' })
export class ProductRepository extends RepositoryBase<ProductBO, ProductModel, ProductDTO, ProductDTO> implements ProductRepositoryContract {
    public constructor(
        http: Http,
        private readonly weaver: Weaver,
        private readonly queryBus: QueryBus
    ) {
        super(http as HttpService);
    }

    protected createBO(): ProductBO {
        return new ProductBO(this.weaver as unknown as WeaverBase, this.queryBus as unknown as QueryBusBase);
    }

    protected async fromPlainObjectAsync(item: ProductDTO): Promise<ProductModel> {
        if (environment.testdev) console.info(`Product info - ID: ${item.id} GUID: ${item.externalId}`);

        const model = new ProductModel(pickBy<ProductModel>({
            id: item.id,
            version: item.version,

            active: item.active,
            description: item.description,
            favourite: item.favourite,
            fiscalName: item.fiscalName,
            kind: item.kind,
            name: item.name,
            externalId: item.externalId,
            pkwiu: item.pkwiu,
            cnCode: item.cnCode,
            offers: [],
            productGroupId: item.groupId,
            jpkVatGroups: item.jpkVatGroups,
            subjectToReverseCharge: item.subjectToReverseCharge,
            unitId: item.unitId,
            vatRateId: item.vatRateId,
            inventoryControl: item.inventoryControl,
            inventoryLevel: item.inventoryLevel,
            inventoryWarningLevel: item.inventoryWarningLevel,
            purchaseVatRateId: item.purchaseVatRateId,
            barCode: item.barCode,
            symbol: item.symbol,

            prices: new ObservableCollection(item.prices.map(price => (new ProductPriceModel(pickBy<ProductPriceModel>({
                net: price.net,
                gross: price.gross,
                currencyId: price.currencyId
            }, val => val !== void 0))))),

            ossVatRates: new ObservableCollection(item.ossVatRates.map(vatRate => (new ProductOssVatRateModel(pickBy<ProductOssVatRateModel>({
                countryId: vatRate.countryId,
                vatRateId: vatRate.vatRateId
            }, val => val !== void 0))))),
        }, val => val !== void 0));

        if (item.purchasePrice) {
            model.purchasePrice = new ProductPriceModel(pickBy<ProductPriceModel>({
                net: item.purchasePrice.net,
                gross: item.purchasePrice.gross,
                currencyId: item.purchasePrice.currencyId
            }, val => val !== void 0));
        }

        return model;
    }

    protected async preparePlainObjectAsync(model: ProductModel): Promise<ProductDTO> {
        return {
            description: model.description,
            favourite: model.favourite,
            fiscalName: model.fiscalName,
            id: model.id,
            version: model.version,
            kind: model.kind,
            name: model.name,
            pkwiu: model.pkwiu,
            cnCode: model.cnCode,
            groupId: model.productGroupId,
            jpkVatGroups: model.jpkVatGroups,
            externalId: model.externalId,
            subjectToReverseCharge: model.subjectToReverseCharge,
            offers: model.offers,
            unitId: model.unitId,
            vatRateId: model.vatRateId,
            inventoryControl: model.inventoryControl,
            inventoryWarningLevel: model.inventoryWarningLevel,
            purchaseVatRateId: model.purchaseVatRateId,
            purchasePrice: model.purchasePrice ? {
                net: model.purchasePrice.net,
                gross: model.purchasePrice.gross,
                currencyId: model.purchasePrice.currencyId
            } : null,
            barCode: model.barCode,
            symbol: model.symbol,

            prices: model.prices.map(item => ({
                net: item.net,
                gross: item.gross,
                currencyId: item.currencyId
            })),

            ossVatRates: model.ossVatRates.map(item => ({
                countryId: item.countryId,
                vatRateId: item.vatRateId
            })),

            productsAssociated: model.associatedEInvoiceSymbols?.map(item => item.externalId) || [],
        };
    }

    public override async deleteAsync(): Promise<DeleteResponse>{
        throw new Error('Method not allowed');
    }
}
